import type { ShelfProduct } from "@/types/shelf";

type WooCommerceImage = {
  src: string;
  alt: string;
};

type WooCommercePrices = {
  price: string;
  currency_code: string;
  currency_minor_unit: number;
};

type WooCommerceStoreProduct = {
  id: number;
  name: string;
  permalink: string;
  is_in_stock: boolean;
  prices: WooCommercePrices;
  images: WooCommerceImage[];
};

const PRODUCTION_PRODUCTS_ENDPOINT =
  "https://sumeru-labs.com/shelf/wp-json/wc/store/v1/products?per_page=100&page=1";

const getProductsEndpoint = () =>
  import.meta.env.DEV && typeof window !== "undefined"
    ? "/shelf-api/wp-json/wc/store/v1/products?per_page=100&page=1"
    : PRODUCTION_PRODUCTS_ENDPOINT;

let firstPageProductsPromise: Promise<ShelfProduct[]> | undefined;

const normalizeProduct = (product: WooCommerceStoreProduct): ShelfProduct => {
  const divisor = 10 ** product.prices.currency_minor_unit;
  const image = product.images[0];

  return {
    id: String(product.id),
    title: product.name,
    availableForSale: product.is_in_stock,
    onlineStoreUrl: product.permalink,
    featuredImage: image ? { url: image.src, altText: image.alt || product.name } : null,
    priceRange: {
      minVariantPrice: {
        amount: (Number(product.prices.price) / divisor).toFixed(product.prices.currency_minor_unit),
        currencyCode: product.prices.currency_code,
      },
    },
  };
};

export const listShelfProductsReq = () => {
  if (!firstPageProductsPromise) {
    firstPageProductsPromise = fetch(getProductsEndpoint())
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`WooCommerce products request failed: ${response.status} ${response.statusText}`);
        }

        const products = (await response.json()) as WooCommerceStoreProduct[];
        return products.map(normalizeProduct);
      })
      .catch((error) => {
        firstPageProductsPromise = undefined;
        throw error;
      });
  }

  return firstPageProductsPromise;
};
